<template>
  <div class="blood-pressure-page">
    <!-- 顶部导航栏 - 参考 bloodOxygen 页面 -->
    <header class="blood-pressure-header" :style="{paddingTop: statusBarHeight + 'px'}">
      <div class="header-background">
        <img src="@/assets/personalInformation/headbackground.png" alt="背景" class="header-bg-img" />
      </div>
      <div class="header-nav">
        <button 
          class="nav-back-btn" 
          @click="goBack"
          :style="{top: pageBackBtnTop + 'px'}"
        >
          <i class="el-icon-arrow-left"></i>
        </button>
        <div class="nav-title">血压</div>
        <div class="nav-placeholder"></div>
      </div>
    </header>

    <!-- 内容区域 -->
    <div class="blood-pressure-content" :style="{paddingTop: pageContentTop + 'px'}">
      <!-- 日/周/月维度切换（保留原有日期筛选） -->
      <div class="segmented-control">
        <div
          v-for="(tab, index) in dimensionTabs"
          :key="'bp-dim-' + index"
          class="segment-item"
          :class="{ active: activeDimension === index }"
          @click="selectDimension(index)"
        >
          {{ tab }}
        </div>
      </div>
      <!-- 血压趋势图卡片 - 参考 bloodOxygen 页面结构 -->
      <div class="bp-chart-card">
        <div class="bp-chart-header">
          <div class="bp-chart-title">
            <i class="bp-chart-icon"></i>
            <span>血压趋势图</span>
          </div>
          <div class="bp-chart-date" @click="timeClick()">
            {{ displayDate }}
            <i class="el-icon-caret-bottom"></i>
          </div>
        </div>
        <div class="health-chart-content">
          <div ref="chart" class="health-chart"></div>
        </div>
        <div class="health-chart-lengend">
          <div class="hcl-item">
            <span :class="queryType === 1 ? 'hcl-theme1' : 'hcl-theme-hi'"></span>
            <p>收缩压</p>
          </div>
          <div class="hcl-item">
            <span :class="queryType === 1 ? 'hcl-theme2' : 'hcl-theme-low'"></span>
            <p>舒张压</p>
          </div>
        </div>
          <!-- 血压状况卡片 -->
        <div class="bp-status-card">
          <div class="bp-status-title">
            <i class="bp-status-icon"></i>
            <span>血压状况</span>
          </div>
          <div class="bp-status-values">
            <div class="bp-status-item">
              <div class="bp-status-label">收缩压 mmHg</div>
              <div class="bp-status-value">{{ name || '--' }}</div>
            </div>
            <div class="bp-status-item">
              <div class="bp-status-label">舒张压 mmHg</div>
              <div class="bp-status-value">{{ name2 || '--' }}</div>
            </div>
          </div>
        </div>

      </div>

     
      <!-- 统计模块 -->
      <div class="health-statistics">
        <div class="hs-title">
          <i class="hs-icon"></i>
          <span>{{ statsTitle }}</span>
        </div>
        <div class="hs-content">
          <div class="hs-cell van-hairline--bottom">
            <div class="hsc-label">平均收缩压</div>
            <div class="hsc-value">{{ avgHi || '--' }}mmHg</div>
          </div>
          <div class="hs-cell van-hairline--bottom">
            <div class="hsc-label">平均舒张压</div>
            <div class="hsc-value">{{ avgLow || '--' }}mmHg</div>
          </div>
          <div class="hs-cell van-hairline--bottom">
            <div class="hsc-label">最高收缩压</div>
            <div class="hsc-value">{{ hi || '--' }}mmHg</div>
          </div>
          <div class="hs-cell">
            <div class="hsc-label">最高舒张压</div>
            <div class="hsc-value">{{ low || '--' }}mmHg</div>
          </div>
        </div>
      </div>
      <div class="health-disclaimer">
        <p>注:手表测血压时，若用户处于运动状态，结果可能存在偏差。</p>
      </div>

      <!-- 说明模块 -->
      <div class="health-desp">
        <div class="hd-title">
          <i class="hd-icon"></i>
          <span>血压</span>
        </div>
        <div class="hd-content">
          <p>血压指血液在血管内流动时作用于单位面积血管壁的侧压力，它是推动血液在血管内流动的动力。一般正常血压的范围：</p>
          <p class="hd-reference-item">90mmHg ≤ 收缩压 &lt; 140mmHg</p>
          <p class="hd-reference-item">60mmHg &lt; 舒张压 &lt; 90mmHg</p>
        </div>
      </div>

      <!-- 免责声明 -->
      <div class="health-disclaimer">
        <p>注:非医疗设备,结果仅供参考,如有不适,请及时就医</p>
        <!-- 聚合页 Tab 引入时：开始分析按钮放在内容最底部（参考血氧页） -->
        <StartAnalysisCta :embedded="embedded" @click="$emit('start-analysis')" />
      </div>

    </div>
    <picke @child-method="parentMethod" :value="value"></picke>
  </div>
</template>
<script>
import { mapState } from 'pinia';
import { useGlobalStore } from '@/stores/global';
import * as echarts from "echarts";
import { bloodpre } from "@/api/activity";
import StartAnalysisCta from '@/components/StartAnalysisCta'
import dayjs from '@/utils/day';
export default {
  name: 'bloodPressure',
  props: {
    /**
     * 是否为聚合页 Tab 内嵌场景（内嵌时才展示开始分析按钮）
     * @type {boolean}
     */
    embedded: { type: Boolean, default: false }
  },
  computed: {
    ...mapState(useGlobalStore, ['statusBarHeight', 'pageContentTop', 'pageBackBtnTop']),
    /**
     * 查询维度：1-日 2-周 3-月
     * @returns {number}
     */
    queryType() {
      return this.activeDimension + 1
    },
    /**
     * 统计标题
     * @returns {string}
     */
    statsTitle() {
      return ['每日统计', '每周统计', '每月统计'][this.activeDimension] || '每日统计'
    },
    /**
     * 格式化显示的日期（日/周/月）
     * @returns {string}
     */
    displayDate() {
      const dateStr = this.resolveSelectedDateStr()
      if (!dateStr) {
        if (this.newtime && this.week) {
          return `${this.newtime} ${this.week}`;
        }
        return '';
      }
      const oDay = dayjs.tz(dateStr)
      if (!oDay.isValid()) {
        if (this.newtime && this.week) {
          return `${this.newtime} ${this.week}`;
        }
        return '';
      }
      if (this.queryType === 2) {
        return `${oDay.isoWeekday(1).format('YYYY年M月D日')} - ${oDay.isoWeekday(7).format('M月D日')}`
      }
      if (this.queryType === 3) {
        return oDay.format('YYYY年M月')
      }
      const weekDays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
      return `${oDay.format('YYYY年M月D日')} ${weekDays[oDay.day()]}`;
    },
  },
  data() {
    return {
      avgLow: "",
      avgHi: "",
      low: "",
      hi: "",
      data: [],
      data1: [],
      nameData: [],
      progress: 100,
      echartsTime: "",
      name: "0",
      name2: "0",
      time: "1",
      time1: "2",
      newtime: "",
      value: false,
      week: "",
      chartInstance: null,
      timeIndex:-1,
      /** 日/周/月 Tab 索引 */
      activeDimension: 0,
      dimensionTabs: ['日', '周', '月']
    };
  },
  components: {
    StartAnalysisCta,
    picke: () => import("@/components/picke"),
  },
  mounted() {
    document.title = "血压趋势";
    if(localStorage.getItem('oldAge') == 1) {
          document.getElementById("html").style.fontSize = "48px";
        }
    // 设置 body 背景色，确保滚动时背景色不丢失
    document.body.style.backgroundColor = '#F2F3F5';
    // 支持消息中心传入 reportDate
    const routeDate =
      this.$route && this.$route.query && this.$route.query.reportDate
        ? String(this.$route.query.reportDate).trim()
        : ''
    if (routeDate) {
      try {
        const d = dayjs.tz(routeDate)
        if (d.isValid()) {
          this.time = this.timestampToDateFormat2(d.valueOf())
        }
      } catch (e) {
        // ignore
      }
    }
  },
  beforeDestroy() {
    // 恢复 body 背景色
    document.body.style.backgroundColor = '';
    // 在组件销毁前移除resize事件监听
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    /**
     * 返回上一页
     */
    goBack() {
      this.$router.go(-1);
    },
    /**
     * 解析当前选中日期为 YYYY-MM-DD
     * @returns {string}
     */
    resolveSelectedDateStr() {
      if (this.time && this.time !== '1') {
        try {
          return this.getTransferTime(this.transferTime(this.time))
        } catch (e) {
          return ''
        }
      }
      return dayjs.tz().format('YYYY-MM-DD')
    },
    /**
     * 切换日/周/月（不影响原有日期选择器）
     * @param {number} index Tab 索引
     * @returns {void}
     */
    selectDimension(index) {
      if (this.activeDimension === index) return
      this.activeDimension = index
      this.name = '0'
      this.name2 = '0'
      this.echartsTime = ''
      this.nameData = []
      this.data = []
      this.data1 = []
      if (this.chartInstance) this.chartInstance.clear()
      this.getData()
    },
    getData() {
      var obj = {
        type: this.queryType,
        dat: this.getTransferTime(this.transferTime(this.time)),
      };
      bloodpre(obj).then((res) => {
        if (res.code == 1000) {
          if (res.data != undefined) {
            this.avgHi = res.data.avgHi || 0;
            this.avgLow = res.data.avgLow || 0;
            this.hi = res.data.hi || 0;
            this.low = res.data.low || 0;
            if (res.data.detailTimes) {
              // 日视图补齐 24:00；周/月保持接口原始刻度
              if (this.queryType === 1) {
                res.data.detailTimes[res.data.detailTimes.length] = "24:00"
              }
              this.nameData = res.data.detailTimes;
              var diyi = 0;
              // 拷贝绝对值序列，避免污染原始数据
              const lowDetails = (res.data.lowDetails || []).slice();
              const hiDetails = (res.data.hiDetails || []).slice();
              for (let i = lowDetails.length - 1; i >= 0; i--) {
                if (lowDetails[i] == 0) {
                  lowDetails[i] = null;
                  hiDetails[i] = null;
                }
               
                if (diyi == 0) {
                  if (lowDetails[i] != 0 && lowDetails[i] != null) {
                    diyi = 1;
                    this.echartsTime = res.data.detailTimes[i];
                    this.name = hiDetails[i];
                    this.name2 = lowDetails[i];
                  }
                }
              }
              this.timeIndex = -1;
              for(let i = 0; i < lowDetails.length; i++) {
                if(lowDetails[i] != null && lowDetails[i] != 0) {
                  this.timeIndex = i
                }
              }
              if (this.queryType === 1) {
                // 日视图：保留原有堆叠面积（低压绝对值 + 高压差值）
                this.data = lowDetails;
                this.data1 = hiDetails.map((hiVal, i) => {
                  if (hiVal == null || lowDetails[i] == null) return null;
                  return hiVal - lowDetails[i];
                });
              } else {
                // 周/月：对齐 serviceLogDetail 收缩压/舒张压绝对值双折线
                this.data = lowDetails;
                this.data1 = hiDetails;
              }
            } else {
              this.nameData = [];
              this.data1 = [];
              this.data = [];
            }
          } else {
            this.avgHi = 0;
            this.avgLow = 0;
            this.hi = 0;
            this.low = 0;
            this.nameData = [];
            this.data1 = [];
            this.data = [];
          }

          this.initChart(this.timeIndex);
        }
        console.log(res.data);
      });
    },
    getTransferTime(data) {
      let str = data.replace(/\//g, "-");
      return str;
    },
    timeLeft() {
      this.week = this.getWeek(
        this.timestampToDateFormat1(
          this.convertDateToTimestamp(this.transferTime(this.time)) - 86400000
        )
      );
      this.leftTime =
        this.convertDateToTimestamp(this.transferTime(this.time)) - 86400000;
      this.time = this.timestampToDateFormat2(this.leftTime);
      this.newtime = this.timestampToDateFormat(this.leftTime);
      this.getData()
      console.log(this.timestampToDateFormat(this.leftTime));
      //console.log(this.leftTime)
      // console.log(this.week)
    },
    convertDateToTimestamp(dateString) {
      var parts = dateString.split("/");
      // 新 Date(年, 月 [, 日 [, 时 [, 分 [, 秒 [, 毫秒]]]]]) 中的月份是从0开始的，所以需要减1
      var date = new Date(parts[0], parts[1] - 1, parts[2]);
      return date.getTime();
    },
    timestampToDateFormat(timestamp) {
      const dateObj = new Date(timestamp); // 创建Date对象
      const year = dateObj.getFullYear(); // 获取年份
      const month = ("0" + (dateObj.getMonth() + 1)).slice(-2); // 获取月份，并补零
      const day = ("0" + dateObj.getDate()).slice(-2); // 获取日期，并补零

      return `${month}月${day}日`; // 返回转换后的日期格式
    },
    timestampToDateFormat1(timestamp) {
      const dateObj = new Date(timestamp); // 创建Date对象
      const year = dateObj.getFullYear(); // 获取年份
      const month = ("0" + (dateObj.getMonth() + 1)).slice(-2); // 获取月份，并补零
      const day = ("0" + dateObj.getDate()).slice(-2); // 获取日期，并补零

      return `${year}/${month}/${day}`; // 返回转换后的日期格式
    },
    timestampToDateFormat2(timestamp) {
      const dateObj = new Date(timestamp); // 创建Date对象
      const year = dateObj.getFullYear(); // 获取年份
      const month = ("0" + (dateObj.getMonth() + 1)).slice(-2); // 获取月份，并补零
      const day = ("0" + dateObj.getDate()).slice(-2); // 获取日期，并补零

      return `${year}年${month}月${day}日`; // 返回转换后的日期格式
    },
    timeRight() {
      this.week = this.getWeek(
        this.timestampToDateFormat1(
        this.convertDateToTimestamp(this.transferTime(this.time)) + 86400000
        )
      );
      this.leftTime =
        this.convertDateToTimestamp(this.transferTime(this.time)) + 86400000;
      this.time = this.timestampToDateFormat2(this.leftTime);
      this.newtime = this.timestampToDateFormat(this.leftTime);
      console.log(this.timestampToDateFormat(this.leftTime));
      this.getData()
    },
    renderCardList(data) {
      console.log(data);
      this.echartsTime = data[0].name;
      if (this.queryType === 1) {
        // 日视图：堆叠面积，收缩压 = 低压 + 差值
        if(data[1].value != undefined) {
          this.name = data[1].value + data[0].value;
        }else {
          this.name = ''
        }
        this.name2 = data[0].value;
      } else {
        // 周/月：绝对值双折线，series[0]=舒张压 data，series[1]=收缩压 data1
        const lowParam = data.find((p) => p.seriesName === '舒张压') || data[0]
        const hiParam = data.find((p) => p.seriesName === '收缩压') || data[1]
        this.name = hiParam && hiParam.value != null ? hiParam.value : ''
        this.name2 = lowParam && lowParam.value != null ? lowParam.value : ''
      }
    },
    initChart(timeIndex) {
      const chartDom = this.$refs.chart;
      this.chartInstance = echarts.init(chartDom);
      const isDay = this.queryType === 1;
      const isMonth = this.queryType === 3;
      const axisLen = (this.nameData && this.nameData.length) || 0;
      const option = {
        title: {},
        tooltip: {
          trigger: "axis",
          extraCssText: "z-index: -1",
          axisPointer: {
            type: "line",
            lineStyle: {
              color: "red",
              width: 1,
              type:'solid'
            },
            
          },
          formatter: (params) => {
            console.log(params);
            let obj = params;
            this.renderCardList(obj);
          },
          clickable: true,
        },
        legend: {
          show: false,
        },
        toolbox: {
          feature: {
            saveAsImage: null,
          },
        },
        grid: {
          x: 15,
          y: isDay ? 15 : 18,
          x2: 30,
          y2: 20,
        },
    xAxis: {
        type: 'category',
        data:  this.nameData,
        boundaryGap: false,
        axisTick: isDay ? {
            alignWithLabel: true,
            interval: function (index, value) {
                return ['00:00', '06:00', '12:00', '18:00', '24:00'].includes(value);
            }
        } : {
            show: false
        },
        axisLabel: isDay ? {
            interval: function (index, value) {
                return ['00:00', '06:00', '12:00', '18:00', '24:00'].includes(value);
            }
        } : {
            // 周：全刻度；月：约每 5 天一个，避免底部过密
            interval: isMonth
              ? (index) => index % 5 === 0 || index === axisLen - 1
              : 0,
            color: '#999999',
            fontSize: 11,
            rotate: 0,
            hideOverlap: true,
            alignMinLabel: 'left',
            alignMaxLabel: 'right'
        }
    },
        yAxis: {
          type: "value",
          scale: true,
          position: "right",
          alignTicks: true,
          axisLine: {
            show: true,
          },
        },
        series: isDay ? [
          {
            name: "舒张压",
            type: "line",
            smooth: true,
            stack: "Total",
            showSymbol: false,
            itemStyle: { color: "#5470DE" },
            lineStyle: { color: "#5470DE" },
            data: this.data,
          },
          {
            name: "收缩压",
            type: "line",
            smooth: true,
            stack: "Total",
            showSymbol: false,
            itemStyle: { color: "#07BEAA" },
            lineStyle: { color: "#07BEAA" },
            areaStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  {
                    offset: 0,
                    color: "#E3EFFE",
                  },
                  {
                    offset: 1,
                    color: "#E5F9FD",
                  },
                ],
                global: false,
              },
            },
            data: this.data1,
          },
        ] : [
          // 周/月：绝对值双折线 + 圆点
          {
            name: "收缩压",
            type: "line",
            data: this.data1,
            smooth: true,
            symbol: "circle",
            symbolSize: 6,
            showSymbol: true,
            lineStyle: { color: "#F76564", width: 1 },
            itemStyle: { color: "#F76564" },
          },
          {
            name: "舒张压",
            type: "line",
            data: this.data,
            smooth: true,
            symbol: "circle",
            symbolSize: 6,
            showSymbol: true,
            lineStyle: { color: "#07BEAA", width: 1 },
            itemStyle: { color: "#07BEAA" },
          },
        ],
      };

      this.chartInstance.setOption(option, true);
      if (timeIndex !== -1) {
          // 显示图标指示器
          this.chartInstance.dispatchAction({
            type: 'showTip',
            seriesIndex: 0,
            dataIndex: timeIndex
          });
        };
      window.addEventListener("resize", this.handleResize);
    },
    handleResize() {
      this.chartInstance.resize();
    },
    timeClick() {
      this.value = !this.value;
      console.log(this.value);
    },
    parentMethod(msg) {
      this.time = msg;
      this.week = this.getWeek(this.transferTime(this.time));
      let originalString = msg;
      this.newtime = originalString.slice(5);
      // this.name = this.data[0];
      // this.name2 = this.data1[0];
      this.getData();
    },
    transferTime(data) {
      let str = data;
      let regex = /年/g; // 使用正则表达式并设置全局标志g
      let regex1 = /月/g; // 使用正则表达式并设置全局标志g
      var newStr = str.replace(regex, "/"); // 替换所有的"World"为"Earth"
      var newStr2 = newStr.replace(regex1, "/"); // 替换所有的"World"为"Earth"
      return newStr2.slice(0, -1);
    },
    getWeek(dateStr) {
      var date = ""; //周几
      let weeks = {
        1: "星期一",
        2: "星期二",
        3: "星期三",
        4: "星期四",
        5: "星期五",
        6: "星期六",
        0: "星期日",
      };
      date = new Date(dateStr);
      let weekIndex = date.getDay();
      date = weeks[weekIndex];
      return date;
    },
  },
};
</script>
<style lang="less" scoped>
* {
  padding: 0;
  font-family: "微软雅黑";
}

/* 页面包装器 */
.blood-pressure-page {
  position: relative;
  min-height: 100vh;
  background: #F2F3F5;
  padding-bottom: 20px;
  
  /* 确保背景色覆盖整个页面，包括滚动区域 */
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #F2F3F5;
    z-index: -1;
    pointer-events: none;
  }
}

/* 头部样式 - 参考 bloodOxygen 页面 */
.blood-pressure-header {
  position: relative;
  width: 100%;
  min-height: 220px;
  overflow: hidden;
  margin-bottom: 0;
}

.header-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.header-bg-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.header-nav {
  position: relative;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  min-height: 44px;
}

.nav-back-btn {
  position: fixed;
  left: 16px;
  z-index: 1000;
  width: 28px;
  height: 28px;
  padding: 0;
  background: #ffffff;
  border: none;
  border-radius: 50%;
  color: #000000;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.nav-back-btn i {
  font-size: 16px;
  font-weight: 600;
  color: #000000;
}

.nav-back-btn:hover {
  background: #f5f5f5;
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.nav-back-btn:active {
  transform: translateY(0) scale(0.95);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.nav-title {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  max-width: calc(100% - 120px);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  text-align: center;
  font-size: 18px;
  font-weight: 500;
  color: #000000;
}

.nav-placeholder {
  flex-shrink: 0;
  width: 28px;
}

/* 内容区域 */
.blood-pressure-content {
  width: 100%;
  padding: 16px;
  position: absolute;
  top: 0px;
  left: 0;
  right: 0;
  z-index: 10;
  background-color: transparent;
}

/* 日/周/月分段控制器 - 参考 heartRateDetails / bloodOxygen */
.segmented-control {
  display: flex;
  background-image: url('~@/assets/heartRate/Rectangle_346242043@2x (1).png');
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 8px;
  padding: 4px;
  margin: 0 0 16px 0;
  gap: 4px;
}

.segment-item {
  flex: 1;
  text-align: center;
  padding: 10px 0;
  font-size: 15px;
  color: #666666;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  font-weight: 400;
  position: relative;
  overflow: hidden;
}

.segment-item.active {
  background-image: url('~@/assets/healthReportList/tab.png');
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
  color: #ffffff;
  font-weight: 500;
}

.segment-item:active {
  opacity: 0.8;
}

/* 血压趋势图卡片 */
.bp-chart-card {
  padding: 16px;
  margin: 0 0 16px 0;
  background-image: url('~@/assets/bloodPressure/Rectangle_853@2x (1).png');
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 12px;
  overflow: hidden;
}

.bp-chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.bp-chart-title {
  display: flex;
  align-items: center;
  
  .bp-chart-icon {
    display: inline-block;
    width: 20px;
    height: 20px;
    margin-right: 8px;
    background-image: url('~@/assets/bloodPressure/Frame_3003@2x (1).png');
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    flex-shrink: 0;
  }
  
  span {
    font-size: 15px;
    font-weight: 500;
    color: #000000;
  }
}

.bp-chart-date {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #000000;
  cursor: pointer;
  
  i {
    margin-left: 4px;
    font-size: 14px;
    color: #666666;
    flex-shrink: 0;
  }
}

/* 图表容器样式 */
.health-chart-content {
  border: 1px solid #F0F0F0;
  border-radius: 10px;
  padding: 16px;
  background: #FFFFFF;
  margin: 0 0 16px 0;
}

.health-chart {
  width: 100%;
  height: 192px;
}

/* 图例样式 */
.health-chart-lengend {
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-top: 8px;
  
  .hcl-item {
    display: flex;
    align-items: center;
    height: 14px;
    
    span {
      display: block;
      width: 20px;
      height: 8px;
      border-radius: 4px;
      &.hcl-theme1 {
        background: #5470DE; // 日视图收缩压色
      }
      &.hcl-theme2 {
        background: #07BEAA; // 日视图舒张压色
      }
      &.hcl-theme-hi {
        background: #F76564; // 周/月收缩压
      }
      &.hcl-theme-low {
        background: #07BEAA; // 周/月舒张压
      }
    }
    
    p {
      margin-left: 4px;
      font-size: 13px;
      color: #151515;
    }
  }
}

/* 血压状况卡片 */
.bp-status-card {
  margin: 15px 0 16px 0;
}

.bp-status-title {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  
  .bp-status-icon {
    display: inline-block;
    width: 20px;
    height: 20px;
    margin-right: 8px;
    background-image: url('~@/assets/bloodPressure/Frame_3964@2x (4).png');
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    flex-shrink: 0;
  }
  
  span {
    font-size: 15px;
    font-weight: 500;
    color: #000000;
  }
}

.bp-status-values {
  display: flex;
  gap: 12px;
}

.bp-status-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: #FFFFFF;
  border-radius: 12px;
  gap: 12px;
  height: 76px;
  box-sizing: border-box;
}

.bp-status-label {
  font-size: 12px;
  color: #666666;
  line-height: 1.2;
}

.bp-status-value {
  font-size: 20px;
  font-weight: bold;
  color: #000000;
  line-height: 1;
}

/* 统计模块样式 - 使用背景图 */
.health-statistics {
  margin: 0 0 16px 0;
  background-image: url('~@/assets/bloodPressure/Rectangle_853@2x (1).png');
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 12px;
  overflow: hidden;
  
  .hs-content {
    margin: 10px;
    background: #FFFFFF;
    border-radius: 10px;
  }
  
  .hs-title {
    display: flex;
    align-items: center;
    padding: 15px 16px 7px 16px;
    font-weight: bold;
    font-size: 15px;
    color: #333333;
    line-height: 18px;
    
    .hs-icon {
      display: inline-block;
      width: 18px;
      height: 18px;
      margin-right: 8px;
      background-image: url('~@/assets/bloodPressure/Frame_3964@2x (5).png');
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
      flex-shrink: 0;
    }
    
    span {
      flex: 1;
    }
  }
  
  .hs-cell {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 44px;
    padding: 0 16px;
    font-size: 14px;
    color: #000000;
    
    .hsc-label {
      font-size: 14px;
      color: #000000;
      font-weight: 400;
    }
    
    .hsc-value {
      font-size: 14px;
      color: #000000;
    }
  }
}

/* 说明模块样式 */
.health-desp {
  margin: 0 0 16px 0;
  background-image: url('~@/assets/bloodPressure/Rectangle_853@2x (3).png');
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 12px;
  padding: 16px;
  overflow: hidden;
  
  .hd-title {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    font-weight: bold;
    font-size: 15px;
    color: #333333;
    
    .hd-icon {
      display: inline-block;
      width: 20px;
      height: 20px;
      margin-right: 8px;
      background-image: url('~@/assets/bloodPressure/Frame_3964@2x (7).png');
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
      flex-shrink: 0;
    }
    
    span {
      flex: 1;
    }
  }
  
  .hd-content {
    padding: 16px;
    background: #FFFFFF;
    border-radius: 12px;
    
    p {
      font-size: 14px;
      color: #000000;
      line-height: 21px;
      text-align: justify;
      margin-bottom: 8px;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
    
    .hd-reference-item {
      margin-bottom: 6px;
    }
  }
}

/* 免责声明 */
.health-disclaimer {
  text-align: center;
  padding: 12px 0;
  font-size: 12px;
  color: #999999;
  line-height: 1.5;
}

.time {
  font-size: 15px;
  color: #636363;
  text-align: center;
}

.down-arrow:after {
  content: "";
  display: block;
  width: 0px;
  height: 0px;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 10px solid #666666;
  margin-top: 10px;
}
.totalBox {
  width: 93%;
  height: 84px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 10px;
  margin-top: 16px;
  padding: 10px 10px;
}
.numberTitle {
  font-family: HiraginoSansGB, HiraginoSansGB;
  font-weight: normal;
  font-size: 22px;
  color: #333333;
  line-height: 33px;
  text-align: center;
  font-style: normal;
}
.nameTitle {
  font-family: HiraginoSansGB, HiraginoSansGB;
  font-weight: normal;
  font-size: 13px;
  color: #999999;
  line-height: 20px;
  text-align: center;
  font-style: normal;
}
.defeat {
  width: 100%;
  height: 64px;
  background: #f3f3f3;
  border-radius: 10px;
  /* margin-top: 16px; */
  display: flex;
  /* justify-content: center;  */
  align-items: left;
}
.drop {
  width: 8px;
  height: 8px;
  background: #209f66;
  border-radius: 100%;
  margin: 0px 10px;
  position: absolute;
  top: 18px;
  left: 2px;
}
.defeatName {
  height: 64px;

  font-family: HiraginoSansGB, HiraginoSansGB;
  font-weight: normal;
  font-size: 16px;
  color: #333333;
  text-align: left;
  font-style: normal;
  /* display: flex; */
  /* justify-content: center; */
  /* align-items: center;  */
  margin-left: 10px;
  padding-left: 28px;
  position: relative;
}
.paceTiele {
  width: 93%;
  /* height: 215px; */
  margin: 0 auto;
  background: #ffffff;
  border-radius: 10px;
  margin-top: 16px;
  padding: 12px 0px;
}
.reachTheStandardTitle {
  font-family: HiraginoSansGB, HiraginoSansGB;
  font-weight: 800;
  font-size: 16px;
  color: #333333;
  line-height: 24px;
  text-align: left;
  font-style: normal;
  padding-left: 16px;
}
.total {
  font-family: PingFangSC, PingFang SC;
  font-weight: 400;
  font-size: 15px;
  color: #000000;
  text-align: left;
  font-style: normal;
  height: 43px;
  line-height: 43px;
  padding-left: 16px;
  border-bottom: 1px solid #e5e5e5;
  position: relative;
  /* margin-top: 5px; */
}
.thisWeek {
  font-family: PingFangSC, PingFang SC;
  font-weight: 400;
  font-size: 15px;
  color: #000000;
  text-align: left;
  font-style: normal;
  height: 43px;
  line-height: 43px;
  padding-left: 16px;
  margin-top: 8px;
}
.lastweek {
  font-family: PingFangSC, PingFang SC;
  font-weight: 400;
  font-size: 15px;
  color: #000000;
  text-align: left;
  font-style: normal;
  height: 43px;
  line-height: 43px;
  padding-left: 16px;
  margin-top: 8px;
}
.continuous {
  font-family: PingFangSC, PingFang SC;
  font-weight: 400;
  font-size: 15px;
  color: #000000;
  text-align: left;
  font-style: normal;
  height: 43px;
  line-height: 43px;
  padding-left: 16px;
  margin-top: 5px;
  position: relative;
}
.day {
  /* float: right;
  margin-right: 8px; */
  position: absolute;
  right: 6px;
  font-size: 14px;
  color: #151515;
}
.weeklySteps {
  margin-top: 16px;
  width: 93%;
  height: 238px;
  background: #ffffff;
  border-radius: 10px;
  padding: 12px 5px;
}
.progressDiv {
  width: 92%;
  margin-top: 1.5px;
  position: relative;
}
.progress-bar {
  width: 100%;
  background-color: #e5e5e5;
  border-radius: 50px;
  overflow: hidden;
}

.progress {
  height: 5px;
  background: linear-gradient(231deg, #00c275 0%, #00b5ba 100%);
  border-radius: 50px;
  /* transition: width 1s ease; */
  margin-left: 0 !important;
}
.last {
  height: 5px;
  background: linear-gradient(231deg, #ffa94a 0%, #ff8e32 100%) !important;
  border-radius: 50px;
  margin-left: 0 !important;
}
.lastDay {
  width: 8px;
  height: 8px;
  background: #ffffff;
  border: 2px solid #ff9315 !important;
  border-radius: 100%;
  position: absolute;
  top: -1.5px;
}
.progressDay {
  width: 8px;
  height: 8px;
  background: #ffffff;
  border: 2px solid #00c275;
  /* transition: width 1s ease; */
  border-radius: 100%;
  position: absolute;
  top: -1.5px;
}
.ssy {
  font-family: HiraginoSansGB, HiraginoSansGB;
  font-weight: normal;
  font-size: 13px;
  color: #151515;
  line-height: 20px;
  text-align: center;
  font-style: normal;
}
.yaliDiv {
  display: flex;
  padding: 0 50px;
}
.xieya {
  font-family: PingFangSC, PingFang SC;
  font-weight: 400;
  font-size: 15px;
  color: #000000;
  line-height: 21px;
  text-align: justify;
  font-style: normal;
  padding: 10px 15px;
}
</style>


// WEBPACK FOOTER //
// src/views/bloodPressure/index.vue